import { jsx, jsxs } from "react/jsx-runtime";
import { forwardRef, useState, useRef, useImperativeHandle, useMemo, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { find, pipe, reject, filter, eq } from "lodash/fp";
import { useId } from "@reach/auto-id";
import "styled-components";
import "../../contexts/display-mode-context/display-mode-context.es.js";
import "../../contexts/global-edit-context/global-edit-context.es.js";
import "../../contexts/toast-context/toast-context.es.js";
import "../../utils/is-valid-ref.es.js";
import "../../hooks/use-horizontal-scroll.es.js";
import "react-use-measure";
import "@juggle/resize-observer";
import "@reach/utils";
import { useOnClickOutside } from "../../hooks/use-on-click-outside.es.js";
import "@reach/rect";
import { useScrollIntoView } from "../../hooks/use-scroll-into-view.es.js";
import { AnimatedPopover } from "../animated-popover/animated-popover.es.js";
import { tokenInputContext } from "./token-input-context.es.js";
import { Token } from "./token.es.js";
import { TokenInputEdit } from "./token-input-edit.es.js";
import { TokenInputWrapper, TokenInputInner, TokenLightbox } from "./token-input.styles.es.js";
import { isControlCharacter } from "../../utils/is-control-character.es.js";
const { Provider } = tokenInputContext;
const propTypes = {
  allowFreeTextTokens: PropTypes.bool,
  autoFocus: PropTypes.bool,
  defaultQuery: PropTypes.string,
  labelKey: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  placeholder: PropTypes.string,
  renderListItem: PropTypes.func.isRequired,
  renderNewListItem: PropTypes.func,
  tooltipKey: PropTypes.string,
  validateInput: PropTypes.func,
  valueKey: PropTypes.string,
  values: PropTypes.arrayOf(PropTypes.string)
};
const defaultProps = {
  allowFreeTextTokens: false,
  autoFocus: false,
  defaultQuery: "",
  labelKey: "label",
  placeholder: "Search",
  renderNewListItem: void 0,
  tooltipKey: "label",
  validateInput: void 0,
  valueKey: "value",
  values: []
};
const TokenInput = forwardRef(
  ({
    allowFreeTextTokens,
    autoFocus,
    defaultQuery,
    labelKey,
    onChange,
    options,
    placeholder,
    renderListItem,
    renderNewListItem,
    tooltipKey,
    validateInput,
    valueKey,
    values
  }, forwardedRef) => {
    const id = useId();
    const [isOpen, setOpen] = useState(false);
    const [query, setQuery] = useState(defaultQuery);
    const [highlightedValue, setHighlightedValue] = useState();
    const [focusedTokenIndex, setFocusedTokenIndex] = useState();
    const [focusedTokenSection, setFocusedTokenSection] = useState();
    const [editingTokenIndex, setEditingTokenIndex] = useState();
    const tokenInputInnerRef = useRef();
    const inputRef = useRef();
    const highlightedRef = useRef();
    const listRef = useRef();
    useImperativeHandle(forwardedRef, () => tokenInputInnerRef.current);
    const selectedOptions = useMemo(
      () => values.map((value) => {
        const option = find({ [valueKey]: value }, options);
        if (option) {
          return option;
        }
        return {
          [valueKey]: value,
          [labelKey]: value,
          [tooltipKey]: value,
          error: validateInput(value),
          isFreeText: true
        };
      }),
      [labelKey, options, tooltipKey, validateInput, valueKey, values]
    );
    const availableOptions = useMemo(
      () => pipe(
        reject((item) => values.includes(item[valueKey])),
        filter(
          (item) => item[labelKey].toLowerCase().includes(query.toLowerCase())
        )
      )(options),
      [labelKey, options, query, valueKey, values]
    );
    const handleClickFreeTextToken = useCallback(
      (value) => {
        setFocusedTokenIndex(void 0);
        setEditingTokenIndex(values.indexOf(value));
        setQuery(value);
      },
      [values]
    );
    const handleRemove = useCallback(
      (value) => {
        onChange(reject(eq(value), values));
      },
      [onChange, values]
    );
    const focusInput = useCallback(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        setFocusedTokenIndex(void 0);
        setOpen(true);
      }
    }, [inputRef, setFocusedTokenIndex]);
    useEffect(() => {
      if (autoFocus) {
        focusInput();
      }
    }, [autoFocus, focusInput, setOpen]);
    const blurInput = () => {
      if (!inputRef.current)
        return;
      inputRef.current.blur();
    };
    const clearEditingToken = useCallback(() => {
      setQuery("");
      setEditingTokenIndex(void 0);
    }, [setEditingTokenIndex]);
    const openMenu = () => {
      if (!isOpen) {
        setOpen(true);
        const firstNonDisabledOption = availableOptions.find(
          (o) => !o.disabled
        );
        if (firstNonDisabledOption) {
          setHighlightedValue(firstNonDisabledOption[valueKey]);
        }
      }
    };
    const handleSelect = useCallback(
      (value) => {
        setQuery("");
        if (values.some((val) => val.toLowerCase() === value.toLowerCase())) {
          return;
        }
        if (editingTokenIndex !== void 0) {
          onChange([
            ...values.slice(0, editingTokenIndex),
            value,
            ...values.slice(editingTokenIndex + 1)
          ]);
        } else {
          onChange([...values, value]);
        }
        clearEditingToken();
      },
      [clearEditingToken, editingTokenIndex, onChange, values]
    );
    const closeMenuAndBlur = useCallback(() => {
      setOpen(false);
      blurInput();
    }, []);
    const onBlurEditingInput = useCallback(() => {
      if (query && editingTokenIndex !== void 0) {
        handleSelect(query);
      } else {
        handleRemove(values[editingTokenIndex]);
      }
      clearEditingToken();
    }, [
      query,
      editingTokenIndex,
      clearEditingToken,
      handleSelect,
      handleRemove,
      values
    ]);
    const handleFocusEditingInput = (event) => {
      event.target.select();
      setOpen(true);
    };
    const handleArrowLeft = useCallback(
      (event) => {
        if (editingTokenIndex !== void 0) {
          if (event.target.selectionStart === 0) {
            onBlurEditingInput();
            setFocusedTokenIndex(
              editingTokenIndex === 0 ? void 0 : editingTokenIndex - 1
            );
            setFocusedTokenSection(
              editingTokenIndex === 0 ? void 0 : "cancel"
            );
          }
        } else if (focusedTokenIndex !== void 0) {
          if (focusedTokenSection === "cancel" && selectedOptions[focusedTokenIndex].isFreeText) {
            setFocusedTokenSection("click");
          } else if (focusedTokenIndex !== 0) {
            setFocusedTokenIndex(focusedTokenIndex - 1);
            setFocusedTokenSection("cancel");
          } else if (focusedTokenIndex === 0) {
            setFocusedTokenIndex(void 0);
            setFocusedTokenSection(void 0);
            setOpen(false);
          }
        } else if (focusedTokenIndex === void 0) {
          if (event.target.selectionStart === 0) {
            if (selectedOptions.length > 0) {
              setFocusedTokenIndex(selectedOptions.length - 1);
              setFocusedTokenSection("cancel");
            } else {
              setOpen(false);
            }
          }
        }
        setHighlightedValue(void 0);
      },
      [
        editingTokenIndex,
        focusedTokenIndex,
        focusedTokenSection,
        onBlurEditingInput,
        selectedOptions,
        setFocusedTokenIndex,
        setFocusedTokenSection
      ]
    );
    const handleArrowRight = useCallback(
      (event) => {
        if (editingTokenIndex !== void 0) {
          if (event.target.selectionStart === query.length) {
            onBlurEditingInput();
            if (editingTokenIndex + 1 === selectedOptions.length) {
              setFocusedTokenIndex(void 0);
              setFocusedTokenSection(void 0);
              focusInput();
            } else {
              setFocusedTokenIndex(
                query ? editingTokenIndex + 1 : editingTokenIndex
              );
              setFocusedTokenSection(
                selectedOptions[editingTokenIndex + 1].isFreeText ? "click" : "cancel"
              );
            }
          }
        } else if (focusedTokenIndex !== void 0) {
          if (focusedTokenIndex === selectedOptions.length - 1) {
            if (focusedTokenSection === "cancel") {
              setFocusedTokenIndex(void 0);
              setFocusedTokenSection(void 0);
              if (availableOptions.length) {
                setHighlightedValue(availableOptions[0][valueKey]);
              }
              event.preventDefault();
              focusInput();
              if (query) {
                inputRef.current.setSelectionRange(query.length, query.length);
              }
            } else {
              setFocusedTokenSection("cancel");
            }
          } else if (focusedTokenSection === "click") {
            setFocusedTokenSection("cancel");
          } else if (focusedTokenSection === "cancel") {
            if (selectedOptions[focusedTokenIndex + 1].isFreeText) {
              setFocusedTokenSection("click");
            }
            setFocusedTokenIndex(focusedTokenIndex + 1);
          }
        } else if (focusedTokenIndex === void 0) {
          setOpen(false);
        }
      },
      [
        availableOptions,
        editingTokenIndex,
        focusInput,
        focusedTokenIndex,
        focusedTokenSection,
        onBlurEditingInput,
        query,
        selectedOptions,
        valueKey
      ]
    );
    const handleKeyDown = useCallback(
      (event) => {
        const highlightedIndex = availableOptions.map((o) => o[valueKey]).indexOf(highlightedValue);
        const optionsCount = availableOptions.length;
        let nextHighlightIndex = highlightedIndex === optionsCount - 1 ? 0 : highlightedIndex + 1;
        let prevHighlightIndex = highlightedIndex <= 0 ? optionsCount - 1 : highlightedIndex - 1;
        if (!event.ctrlKey && !event.metaKey && !isControlCharacter(event.key)) {
          event.stopPropagation();
        }
        switch (event.key) {
          case "Backspace":
            if (focusedTokenIndex !== void 0) {
              event.preventDefault();
              handleRemove(values[focusedTokenIndex]);
              if (values.length && focusedTokenIndex !== 0) {
                setFocusedTokenIndex(focusedTokenIndex - 1);
              } else {
                focusInput();
              }
            } else if (editingTokenIndex !== void 0 && !query) {
              onBlurEditingInput();
              focusInput();
            } else if (editingTokenIndex === void 0 && !query) {
              handleRemove(values[values.length - 1]);
            }
            break;
          case "Escape":
            if (editingTokenIndex !== void 0) {
              onBlurEditingInput();
              focusInput();
            } else if (query) {
              if (allowFreeTextTokens) {
                handleSelect(query);
              } else {
                setQuery("");
              }
            }
            closeMenuAndBlur();
            break;
          case "Enter":
            if (allowFreeTextTokens && editingTokenIndex !== void 0) {
              if (!query) {
                handleRemove(values[editingTokenIndex]);
              } else if (highlightedValue && availableOptions.length > 0) {
                handleSelect(availableOptions[highlightedValue]);
              } else {
                handleSelect(query);
              }
              focusInput();
            } else if (allowFreeTextTokens && query && (query === highlightedValue || availableOptions.length === 0)) {
              handleSelect(query);
            } else if (highlightedValue) {
              handleSelect(highlightedValue);
              const firstNonDisabledOption = availableOptions.find(
                (opt) => opt[valueKey] !== highlightedValue && !opt.disabled
              );
              if (firstNonDisabledOption) {
                setHighlightedValue(firstNonDisabledOption[valueKey]);
              }
            }
            break;
          case "ArrowDown":
            event.preventDefault();
            if (!editingTokenIndex) {
              setFocusedTokenIndex(void 0);
              setFocusedTokenSection(void 0);
              focusInput();
            }
            if (!allowFreeTextTokens && !availableOptions.find((o) => !o.disabled)) {
              setHighlightedValue(void 0);
              break;
            }
            if (allowFreeTextTokens && (highlightedIndex === optionsCount - 1 || availableOptions.every((o) => o.disabled))) {
              if (query) {
                setHighlightedValue(query);
                break;
              }
            }
            while (availableOptions[nextHighlightIndex].disabled) {
              nextHighlightIndex = nextHighlightIndex === optionsCount - 1 ? 0 : nextHighlightIndex + 1;
            }
            setHighlightedValue(availableOptions[nextHighlightIndex][valueKey]);
            break;
          case "ArrowUp":
            event.preventDefault();
            if (!editingTokenIndex) {
              setFocusedTokenIndex(void 0);
              setFocusedTokenSection(void 0);
              focusInput();
            }
            if (!allowFreeTextTokens && !availableOptions.find((o) => !o.disabled)) {
              setHighlightedValue(void 0);
            } else if (allowFreeTextTokens && query && (highlightedIndex === 0 || availableOptions.every((o) => o.disabled))) {
              setHighlightedValue(query);
            } else {
              while (availableOptions[prevHighlightIndex].disabled) {
                prevHighlightIndex = prevHighlightIndex <= 0 ? optionsCount - 1 : prevHighlightIndex - 1;
              }
              setHighlightedValue(
                availableOptions[prevHighlightIndex][valueKey]
              );
            }
            break;
          case "Tab":
            if (event.shiftKey) {
              handleArrowLeft(event);
            } else {
              handleArrowRight(event);
            }
            break;
          case "ArrowLeft":
            handleArrowLeft(event);
            break;
          case "ArrowRight":
            handleArrowRight(event);
            break;
        }
      },
      [
        allowFreeTextTokens,
        availableOptions,
        closeMenuAndBlur,
        editingTokenIndex,
        focusInput,
        focusedTokenIndex,
        handleArrowLeft,
        handleArrowRight,
        handleRemove,
        handleSelect,
        highlightedValue,
        onBlurEditingInput,
        query,
        valueKey,
        values
      ]
    );
    useEffect(() => {
      if (!allowFreeTextTokens && query) {
        setOpen(availableOptions.length > 0);
      }
      if (query) {
        const firstNonDisabledOption = availableOptions.find(
          (o) => !o.disabled
        );
        if (firstNonDisabledOption) {
          setHighlightedValue(firstNonDisabledOption[valueKey]);
        } else if (allowFreeTextTokens) {
          setHighlightedValue(query);
        }
      }
    }, [
      allowFreeTextTokens,
      availableOptions,
      editingTokenIndex,
      query,
      setHighlightedValue,
      valueKey
    ]);
    useOnClickOutside(
      [tokenInputInnerRef],
      useCallback(() => {
        if (editingTokenIndex !== void 0) {
          onBlurEditingInput();
        }
        closeMenuAndBlur();
        setQuery("");
      }, [closeMenuAndBlur, editingTokenIndex, onBlurEditingInput])
    );
    const onClickWrapper = useCallback(() => {
      if (editingTokenIndex !== void 0) {
        if (query && !highlightedValue) {
          onBlurEditingInput();
        }
      }
      focusInput();
    }, [
      editingTokenIndex,
      focusInput,
      highlightedValue,
      onBlurEditingInput,
      query
    ]);
    const handleInnerFocus = useCallback(() => {
      if (focusedTokenIndex === void 0 && editingTokenIndex === void 0) {
        focusInput();
      }
    }, [editingTokenIndex, focusedTokenIndex, focusInput]);
    const contextValue = useMemo(
      () => ({
        handleHighlight: (value) => {
          if (query === value || !availableOptions.find((opt) => opt[valueKey] === value).disabled) {
            setHighlightedValue(value);
          }
        },
        handleSelect,
        highlightedValue
      }),
      [availableOptions, handleSelect, highlightedValue, query, valueKey]
    );
    useScrollIntoView(listRef, highlightedRef);
    return /* @__PURE__ */ jsx(Provider, { value: contextValue, children: /* @__PURE__ */ jsxs(TokenInputWrapper, { onKeyDown: handleKeyDown, onClick: onClickWrapper, children: [
      /* @__PURE__ */ jsxs(
        TokenInputInner,
        {
          ref: tokenInputInnerRef,
          "aria-activedescendant": isOpen ? `token-input__listbox_${id}` : void 0,
          "aria-controls": `token-input__listbox_${id}`,
          "aria-expanded": isOpen,
          onFocus: handleInnerFocus,
          role: "combobox",
          children: [
            selectedOptions.map(
              (item, index) => editingTokenIndex === index ? /* @__PURE__ */ jsx(
                TokenInputEdit,
                {
                  autoFocus: true,
                  onChange: setQuery,
                  onFocus: handleFocusEditingInput,
                  value: query
                },
                item[valueKey]
              ) : /* @__PURE__ */ jsx(
                Token,
                {
                  autoFocus: index === focusedTokenIndex ? setFocusedTokenSection : void 0,
                  error: item.error,
                  isFreeText: item.isFreeText,
                  onClick: () => {
                    handleClickFreeTextToken(item[valueKey]);
                  },
                  onRemove: () => handleRemove(item[valueKey]),
                  tooltip: item.error || item[tooltipKey],
                  children: item[labelKey]
                },
                item[valueKey]
              )
            ),
            editingTokenIndex === void 0 && /* @__PURE__ */ jsx(
              TokenInputEdit,
              {
                onChange: setQuery,
                onFocus: openMenu,
                placeholder: values.length === 0 ? placeholder : void 0,
                ref: inputRef,
                value: query
              }
            )
          ]
        }
      ),
      /* @__PURE__ */ jsx(
        AnimatedPopover,
        {
          justify: "stretch",
          offset: 4,
          open: isOpen,
          placement: "bottom",
          targetRef: tokenInputInnerRef,
          children: isOpen && (availableOptions.length > 0 || allowFreeTextTokens && query) && /* @__PURE__ */ jsxs(
            TokenLightbox,
            {
              id: `token-input__listbox_${id}`,
              ref: listRef,
              role: "listbox",
              children: [
                availableOptions.map((option) => /* @__PURE__ */ jsx(
                  "div",
                  {
                    role: "option",
                    "aria-selected": "false",
                    ref: highlightedValue === option[valueKey] ? highlightedRef : void 0,
                    children: renderListItem(option)
                  },
                  option[valueKey]
                )),
                allowFreeTextTokens && query && renderNewListItem(query)
              ]
            }
          )
        }
      )
    ] }) });
  }
);
TokenInput.propTypes = propTypes;
TokenInput.defaultProps = defaultProps;
export {
  TokenInput
};
